<script setup>
  import {computed, reactive, ref, watch} from "vue";

  import ComponentIcon from "@components/Icons/Icon.vue";
  import {MedSpinner} from "@pharma4u/medicheck-components";
  import {debounce} from "es-toolkit";

  defineOptions({
    name: "PatientinputComponentAutocomplete",
  });

  const props = defineProps({
    placeholder: {
      type: String,
      default: "",
    },
    fetchMethod: {
      type: Function,
      default: null,
    },
    minSearchTermLength: {
      type: Number,
      default: 4,
    },
  });

  const emit = defineEmits(["selected"]);

  const refInput = ref(null);
  const refResultList = ref(null);

  const fieldId = ref(crypto.randomUUID());
  const state = reactive({
    searchTerm: null,
    results: [],
    isLoading: false,
    isError: false,
  });

  const hasSearchTerm = computed(() => state.searchTerm?.trim().length >= props.minSearchTermLength);

  const showEmptySlot = computed(() => hasSearchTerm.value && !state.isLoading && state.results.length === 0);

  watch(
    () => state.searchTerm,
    (newSearchTerm) => {
      state.isLoading = !!newSearchTerm && newSearchTerm.length >= 4;
    },
  );

  watch(
    () => state.searchTerm,
    debounce(() => {
      if (hasSearchTerm.value === false) {
        state.results = [];
        state.isLoading = false;
        return;
      }

      state.isLoading = true;
      state.isError = false;

      props
        .fetchMethod(state.searchTerm)
        .then((res) => {
          if (hasSearchTerm.value === false) {
            state.isLoading = false;
          } else if (res === false) {
            // a cancel token ended the former request and we keep waiting
            state.isLoading = true;
          } else if (typeof state.results == "object") {
            state.isLoading = false;
            state.results = res;
          }
        })
        .catch(() => {
          state.isLoading = false;
          state.isError = true;
          reset();
        });
    }, 1000),
  );

  const handleFocusOut = (event) => {
    if (event.relatedTarget && ![...(refResultList.value?.children ?? [])].includes(event.relatedTarget)) {
      reset();
    }
  };

  const onClickOutside = () => {
    reset();
  };

  const selectItem = (item) => {
    emit("selected", item);
    reset();
  };

  const reset = () => {
    state.results = [];
    state.searchTerm = null;
  };

  const focus = () => {
    setTimeout(() => {
      refInput.value.focus();
    }, 10);
  };

  const selectPrevious = (event) => {
    event.preventDefault();

    const index = [...refResultList.value.children].indexOf(event.target);

    if (index > 0) {
      refResultList.value.children.item(index - 1).focus();
    } else if (index === 0) {
      refResultList.value.children.item(refResultList.value.children.length - 1).focus();
    }
  };

  const selectNext = (event) => {
    event.preventDefault();

    const index = [...refResultList.value.children].indexOf(event.target);

    if (index === -1 && state.results.length > 0) {
      refResultList.value.children.item(0).focus();
    } else if (index < refResultList.value.children.length - 1) {
      refResultList.value.children.item(index + 1).focus();
    } else if (index === refResultList.value.children.length - 1) {
      refResultList.value.children.item(0).focus();
    }
  };

  const setFocus = () => {
    refInput.value.focus();
  };

  defineExpose({
    focus,
  });
</script>

<template>
  <div class="rounded-md border border-gray-300 bg-white px-6 py-4">
    <div class="flex border-b-1 border-gray-300 focus-within:border-mcred">
      <div class="flex w-10 items-end justify-center pb-2">
        <MedSpinner v-if="state.isLoading" size="lg" :data-test="getTestId('autocomplete-spinner')" />

        <ComponentIcon
          v-else
          class="rotate-90 cursor-pointer text-2xl text-mcred"
          :test-id="getTestId('autocomplete-search')"
          @click="setFocus"
        >
          search
        </ComponentIcon>
      </div>

      <div class="w-full">
        <input
          :id="fieldId"
          ref="refInput"
          v-model="state.searchTerm"
          v-click-outside="onClickOutside"
          autocomplete="off"
          type="text"
          class="h-9 border-0! pl-0 text-base! focus:border-0!"
          :placeholder="placeholder"
          :data-test="getTestId('autocomplete-input')"
          @focusout="handleFocusOut"
          @keydown.down="selectNext"
          @keydown.tab="onClickOutside"
        />
      </div>
    </div>

    <div v-if="showEmptySlot" class="mt-3" :data-test="getTestId('autocomplete-dropdown-empty')">
      Zu Ihrer Eingabe konnte nichts gefunden werden.
    </div>

    <div v-else-if="state.isError" class="mt-3" :data-test="getTestId('autocomplete-dropdown-error-message')">
      Beim Suchen ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.
    </div>

    <div v-else-if="state.results.length > 0">
      <ul
        ref="refResultList"
        tabindex="-1"
        role="listbox"
        class="max-h-60 overflow-y-auto rounded-b-md border-x border-b border-gray-300 bg-white px-6 py-1"
      >
        <li
          v-for="(item, index) in state.results"
          :id="`resultList${index}`"
          :key="index"
          role="option"
          tabindex="-1"
          class="z-10 cursor-pointer rounded border-1 border-white px-3 py-1 text-gray-900 hover:bg-gray-100 focus:border-1 focus:border-mcred focus-visible:outline-none"
          :data-test="getTestId(`autocomplete-dropdown-item-${index}`)"
          aria-selected="false"
          @click="selectItem(item)"
          @keydown.enter="selectItem(item)"
          @keydown.up="selectPrevious"
          @keydown.down="selectNext"
          @keydown.tab="onClickOutside"
        >
          <slot name="autocomplete-item" :item="item" :term="state.searchTerm" />
        </li>
      </ul>
    </div>

    <slot name="after-search" />
  </div>
</template>
